import set from 'lodash/set';
import uniqBy from 'lodash/uniqBy';
import { useQuery } from '@apollo/react-hooks';
import type { NetworkStatus, ApolloError } from 'apollo-client';

import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data';
import type { GlobalPageCard } from '@confluence/page-card';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils';
import { fg } from '@confluence/feature-gating';

import type { LoadingState } from '../homeViewsQueryHelpers';
import {
	getLoadingState,
	notEmpty,
	byTitle,
	notDeleted,
	isCreatorCurrentUser,
} from '../homeViewsQueryHelpers';
import { transformRecentToPageCard, transformCreatedByMeToPageCard } from '../pageCardTransformers';

import { getRecentQueryOptions } from './recentQueryHelpers';
import type {
	HomeRecentActivitiesQuery as HomeRecentActivitiesQueryType,
	HomeRecentActivitiesQueryVariables,
} from './__types__/HomeRecentActivitiesQuery';
import type {
	HomeRecentCreatedQuery as HomeRecentCreatedQueryType,
	HomeRecentCreatedQueryVariables,
} from './__types__/HomeRecentCreatedQuery';

type UseRecentData = ({
	pill,
	filterText,
	translatedUntitled,
}: {
	pill?: string;
	filterText: string;
	translatedUntitled: string;
}) => {
	pageCardData: GlobalPageCard[];
	loading: LoadingState;
	networkStatus: NetworkStatus;
	hasNextPage: boolean;
	loadMore: () => void;
	error?: ApolloError;
};

export type QueryResult = HomeRecentActivitiesQueryType | HomeRecentCreatedQueryType | undefined;
export type QueryVariables = HomeRecentActivitiesQueryVariables | HomeRecentCreatedQueryVariables;

const isMyActivities = (data: QueryResult): data is HomeRecentActivitiesQueryType => {
	if (!data) {
		return false;
	}
	return (data as HomeRecentActivitiesQueryType).activities !== undefined;
};

const isSearch = (data: QueryResult): data is HomeRecentCreatedQueryType => {
	if (!data) {
		return false;
	}
	return (data as HomeRecentCreatedQueryType).search !== undefined;
};

const normalizeData = (
	data: QueryResult,
	filterText: string,
	translatedUntitled: string,
	userId: string | null,
	isWhiteboardEmojiFGEnabled: boolean,
) => {
	let pageCardData: GlobalPageCard[] = [];
	let hasNextPage = false;
	let endCursor = '';

	if (isMyActivities(data)) {
		const all = data?.activities?.myActivities?.all;
		pageCardData = (all?.edges || [])
			.map((edge) => edge?.node)
			.filter(notEmpty)
			.filter((node) => notEmpty(node.object?.content))
			.filter((node) => notDeleted(node.object?.content))
			.map((node) =>
				transformRecentToPageCard(node, translatedUntitled, isWhiteboardEmojiFGEnabled),
			)
			.filter((node) => byTitle(filterText, node.title.text));
		hasNextPage = all?.pageInfo.hasNextPage || false;
		endCursor = (all?.edges && all?.edges[all.edges?.length - 1]?.cursor) || '';
	} else if (isSearch(data)) {
		pageCardData = (data.search?.nodes || [])
			.filter(notEmpty)
			.filter((node) => notEmpty(node.content))
			.filter((node) => notDeleted(node.content))
			// We need the filter below so that when the gate is off, content the user has published but not created is not shown. When the gate is on, we want to show content the user also owns
			.filter(
				(node) =>
					isCreatorCurrentUser(userId, node.content) ||
					expVal<boolean>('confluence_frontend_page_ownership_recent_tab_page', 'isEnabled', false),
			)
			.map((node) =>
				transformCreatedByMeToPageCard(
					node,
					translatedUntitled,
					userId,
					expVal<boolean>('confluence_frontend_page_ownership_recent_tab_page', 'isEnabled', false),
				),
			)
			.filter((node) => byTitle(filterText, node.title.text));
		hasNextPage = data.search?.pageInfo?.hasNextPage || false;
		endCursor = data.search?.pageInfo?.endCursor || '';
	}

	pageCardData = uniqBy(pageCardData, (pageCard) => pageCard.id);

	return {
		pageCardData,
		hasNextPage,
		endCursor,
	};
};

export const useRecentData: UseRecentData = ({ pill, filterText, translatedUntitled }) => {
	const { cloudId, userId } = useSessionData();
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardViewedEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isWhiteboardUpdatedEnabled = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isDatabaseEnabled = isContentTypeEnabledInCurrentEnv('database');

	const { query, variables } = getRecentQueryOptions({
		pill,
		cloudId,
		endCursor: '',
		isWhiteboardViewedEnabled,
		isWhiteboardUpdatedEnabled,
		isDatabaseEnabled,
	});

	const { data, loading, error, networkStatus, fetchMore } = useQuery<QueryResult, QueryVariables>(
		query,
		{
			variables,
			notifyOnNetworkStatusChange: true,
		},
	);

	const { pageCardData, hasNextPage, endCursor } = normalizeData(
		data,
		filterText,
		translatedUntitled,
		userId,
		fg('confluence_frontend_whiteboard_emoji_titles'),
	);

	const loadMore = () => {
		if (hasNextPage && endCursor && !loading) {
			void fetchMore({
				variables: getRecentQueryOptions({
					pill,
					cloudId,
					endCursor,
					isWhiteboardViewedEnabled,
					isWhiteboardUpdatedEnabled,
					isDatabaseEnabled,
				}).variables,
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev;
					}
					const result = { ...fetchMoreResult };
					if (isMyActivities(prev) && isMyActivities(fetchMoreResult)) {
						const prevEdges = prev.activities?.myActivities?.all?.edges || [];
						const fetchMoreEdges = fetchMoreResult.activities?.myActivities?.all?.edges || [];
						const mergedEdges = [...prevEdges, ...fetchMoreEdges];
						set(result, 'activities.myActivities.all.edges', mergedEdges);
					} else if (isSearch(prev) && isSearch(fetchMoreResult)) {
						const prevNodes = prev.search?.nodes || [];
						const fetchMoreNodes = fetchMoreResult.search?.nodes || [];
						const mergedNodes = [...prevNodes, ...fetchMoreNodes];
						set(result, 'search.nodes', mergedNodes);
					}

					return result;
				},
			});
		}
	};

	return {
		pageCardData,
		loading: getLoadingState(networkStatus),
		networkStatus,
		error,
		hasNextPage,
		loadMore,
	};
};
